<template>
  <div class="nav-step">
    <!-- 原有导航栏 -->
    <div class="nav-container">
      <div class="nav-item" v-for="(item, index) in props.data" :key="index">
        <a :href="item.link" class="nav-link">
          <span class="nav-label">{{ item.label }}</span>
        </a>
        <div class="nav-divider" v-if="index < props.data.length - 1"></div>
      </div>
    </div>

    <!-- 新增：单位信息展示区（支持后台动态渲染） -->
    <div class="org-info-container">
      <!-- 主办单位 -->
      <div class="org-info-item">
        <h4 class="org-title">主办单位</h4>
        <div class="org-content">
          <p class="org-name">{{ props.orgData?.host?.name || '待补充主办单位' }}</p>

          <div class="org-contact">
            <span>联系人：{{ props.orgData?.host?.contactPerson || '无' }}</span>
            <span>联系电话：{{ props.orgData?.host?.phone || '无' }}</span>
            <span>地址：{{ props.orgData?.host?.address || '无' }}</span>
          </div>

          <div class="org-intro">
            <h5>简介：</h5>
            <p class="intro-text">{{ props.orgData?.host?.introText || '暂无简介' }}</p>

            <div class="intro-images" v-if="props.orgData?.host?.introImages && props.orgData.host.introImages.length">
              <img
                  v-for="(img, idx) in props.orgData.host.introImages"
                  :key="idx"
                  :src="img"
                  :alt="`主办单位图片${idx+1}`"
                  class="intro-img"
              @click="openImageViewer(img, props.orgData.host.introImages)"
              />
            </div>

            <div class="intro-video" v-if="props.orgData?.host?.introVideo">
              <video
                  :src="props.orgData.host.introVideo"
                  controls
                  class="intro-video-player"
                  poster="https://picsum.photos/id/1062/800/450"
              >
                您的浏览器不支持视频播放
              </video>
            </div>

            <a
                :href="props.orgData?.host?.officialLink"
                target="_blank"
                class="official-link"
                v-if="props.orgData?.host?.officialLink"
            >
              访问主办单位官网 →
            </a>
          </div>
        </div>
      </div>

      <!-- 承办单位 -->
      <div class="org-info-item">
        <h4 class="org-title">承办单位</h4>
        <div class="org-content">
          <p class="org-name">{{ props.orgData?.undertake?.name || '待补充承办单位' }}</p>

          <div class="org-contact">
            <span>联系人：{{ props.orgData?.undertake?.contactPerson || '无' }}</span>
            <span>联系电话：{{ props.orgData?.undertake?.phone || '无' }}</span>
            <span>地址：{{ props.orgData?.undertake?.address || '无' }}</span>
          </div>

          <div class="org-intro">
            <h5>简介：</h5>
            <p class="intro-text">{{ props.orgData?.undertake?.introText || '暂无简介' }}</p>

            <div class="intro-images" v-if="props.orgData?.undertake?.introImages && props.orgData.undertake.introImages.length">
              <img
                  v-for="(img, idx) in props.orgData.undertake.introImages"
                  :key="idx"
                  :src="img"
                  :alt="`承办单位图片${idx+1}`"
                  class="intro-img"
              @click="openImageViewer(img, props.orgData.undertake.introImages)"
              />
            </div>

            <div class="intro-video" v-if="props.orgData?.undertake?.introVideo">
              <video
                  :src="props.orgData.undertake.introVideo"
                  controls
                  class="intro-video-player"
                  poster="https://picsum.photos/id/1063/800/450"
              >
                您的浏览器不支持视频播放
              </video>
            </div>

            <a
                :href="props.orgData?.undertake?.officialLink"
                target="_blank"
                class="official-link"
                v-if="props.orgData?.undertake?.officialLink"
            >
              访问承办单位官网 →
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片放大预览层（新增左右切换按钮） -->
    <div
        class="image-viewer"
        v-if="isImageViewerOpen"
        @click="closeImageViewer"
    >
      <!-- 左切换按钮（只有多张图才显示） -->
      <button
          class="viewer-btn viewer-prev"
          @click.stop="switchImage(-1)"
          v-if="imageList.length > 1"
      >
        ←
      </button>

      <!-- 预览图片 -->
      <img
          class="viewer-img"
          :src="currentImage"
          :alt="`${currentImageIndex + 1}/${imageList.length} 放大预览图`"
          @click.stop
      />

      <!-- 右切换按钮（只有多张图才显示） -->
      <button
          class="viewer-btn viewer-next"
          @click.stop="switchImage(1)"
          v-if="imageList.length > 1"
      >
        →
      </button>

      <!-- 图片页码（只有多张图才显示） -->
      <div class="viewer-pagination" v-if="imageList.length > 1">
        {{ currentImageIndex + 1 }} / {{ imageList.length }}
      </div>

      <!-- 关闭按钮 -->
      <span class="viewer-close" @click="closeImageViewer">×</span>
    </div>
  </div>
</template>

<script setup>
import { defineProps, onMounted, ref, onUnmounted } from 'vue';

const props = defineProps({
  data: { type: Array, required: true },
  theme: { type: Object, required: true },
  orgData: {
    type: Object,
    required: false,
    default: () => ({
      host: {},
      undertake: {}
    })
  }
});

// 新增：图片轮播相关状态
const isImageViewerOpen = ref(false); // 预览层是否显示
const currentImage = ref(''); // 当前放大的图片地址
const imageList = ref([]); // 当前单位的所有图片数组
const currentImageIndex = ref(0); // 当前图片在数组中的索引

// 新增：打开图片预览（接收当前图片和图片数组）
const openImageViewer = (imgSrc, imgList) => {
  currentImage.value = imgSrc;
  imageList.value = imgList; // 存储当前单位的所有图片
  currentImageIndex.value = imgList.findIndex(img => img === imgSrc); // 计算当前图片索引
  isImageViewerOpen.value = true;
  document.body.style.overflow = 'hidden'; // 禁止页面滚动
};

// 新增：关闭图片预览
const closeImageViewer = () => {
  isImageViewerOpen.value = false;
  currentImage.value = '';
  imageList.value = [];
  currentImageIndex.value = 0;
  document.body.style.overflow = ''; // 恢复页面滚动
};

// 新增：切换图片（dir：-1=上一张，1=下一张）
const switchImage = (dir) => {
  let newIndex = currentImageIndex.value + dir;
  // 循环切换：最后一张切到第一张，第一张切到最后一张
  if (newIndex < 0) {
    newIndex = imageList.value.length - 1;
  } else if (newIndex >= imageList.value.length) {
    newIndex = 0;
  }
  // 更新当前图片和索引
  currentImageIndex.value = newIndex;
  currentImage.value = imageList.value[newIndex];
};

// 新增：ESC 键关闭预览
const handleEscKey = (e) => {
  if (e.key === 'Escape' && isImageViewerOpen.value) {
    closeImageViewer();
  }
};

// 监听与移除监听
onMounted(() => {
  console.log('NavStep 拿到的 orgData：', props.orgData);
  window.addEventListener('keydown', handleEscKey);
});

onUnmounted(() => {
  window.removeEventListener('keydown', handleEscKey);
});
</script>

<style scoped lang="scss">
$primary: #FF4D7D;
$secondary: #FF9EB5;

.nav-step {
  background: white;
  box-shadow: 0 2px 8px rgba(255, 77, 125, 0.1);
  position: sticky;
  top: 0;
  z-index: 999;

  .nav-container {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    padding: 16px 20px;
    border-bottom: 1px solid #f5f5f5;
  }

  .nav-item {
    display: flex;
    align-items: center;
    margin: 0 8px;
  }

  .nav-link {
    padding: 8px 16px;
    border-radius: 20px;
    transition: all 0.3s ease;

    .nav-label {
      font-size: 16px;
      font-weight: 500;
    }

    &:hover {
      background-color: $secondary;
      color: white;
    }
  }

  .nav-divider {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: $secondary;
    margin-left: 16px;
  }

  .org-info-container {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .org-info-item {
    margin-bottom: 30px;
    padding: 20px;
    border-radius: 12px;
    background-color: #fafafa;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .org-title {
    font-size: 18px;
    font-weight: 600;
    color: $primary;
    margin-bottom: 15px;
    padding-bottom: 8px;
    border-bottom: 2px solid $secondary;
  }

  .org-content {
    font-size: 14px;
    color: #333;
  }

  .org-name {
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 10px;
  }

  .org-contact {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 15px;
    color: #666;
  }

  .org-intro {
    margin-top: 15px;

    h5 {
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 8px;
    }

    .intro-text {
      line-height: 1.8;
      color: #666;
      margin-bottom: 15px;
    }

    .intro-images {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
      margin-bottom: 15px;
    }

    .intro-img {
      width: 120px;
      height: 80px;
      object-fit: cover;
      border-radius: 6px;
      cursor: pointer;
      transition: transform 0.3s ease;
      border: 2px solid transparent;

      &:hover {
        transform: scale(1.05);
        border-color: $secondary;
      }
    }

    .intro-video {
      margin-bottom: 15px;

      .intro-video-player {
        width: 100%;
        max-width: 100%;
        height: auto;
        border-radius: 8px;
      }
    }

    .official-link {
      display: inline-block;
      color: $primary;
      text-decoration: underline;
      transition: color 0.3s ease;

      &:hover {
        color: $secondary;
        text-decoration: none;
      }
    }
  }
}

.image-viewer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
  transition: opacity 0.3s ease;

  .viewer-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    border: none;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background-color: $secondary;
      transform: translateY(-50%) scale(1.1);
    }
  }

  .viewer-prev {
    left: 30px;
  }

  .viewer-next {
    right: 30px;
  }

  .viewer-img {
    max-width: 90%;
    max-height: 90%;
    border-radius: 8px;
    object-fit: contain;
    box-shadow: 0 0 30px rgba(255, 255, 255, 0.3);
  }

  .viewer-close {
    position: absolute;
    top: 20px;
    right: 30px;
    color: white;
    font-size: 36px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      color: $secondary;
      transform: scale(1.2);
    }
  }

  .viewer-pagination {
    position: absolute;
    bottom: 30px;
    color: white;
    font-size: 18px;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 8px 16px;
    border-radius: 20px;
  }
}
</style>